import { AlertTriangle, Check, X } from "lucide-react";
import { useMemo } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ACCESS_CATALOG } from "../../constants/accessCatalog.constants";
import type { GatheredStatement } from "../../types/access.types";
import { resolveCell } from "../../utils/denyFirst";

export interface EffectiveMatrixProps {
  /** The principal's gathered statements (roles → policies → statements). */
  statements: readonly GatheredStatement[];
  className?: string;
}

/**
 * Per-module effective-access grid. For every catalog module it resolves each atomic action deny-first and
 * renders an allow / deny / none chip. A `*` marks a deny that overrode an allow. Read-only — mirrors the
 * design's matrix using the real backend catalog.
 */
export function EffectiveMatrix({ statements, className }: EffectiveMatrixProps) {
  const { t } = useTranslations("settings");

  /**
   * Resolve all 84 cells once per statement set instead of on every render.
   *
   * `resolveCell` scans the whole statement array per cell, so the grid is O(cells × statements): with
   * `full_admin_access` selected that is ~7 000 passes, and it sits inside two drawers whose local state
   * (search box, toggles, pending id) re-renders on every keystroke. The parents already memoize
   * `statements`, so keying on it is enough.
   */
  const rows = useMemo(
    () =>
      ACCESS_CATALOG.map((mod) => {
        const cells = mod.actions.map((atom) => ({ atom, res: resolveCell(statements, mod.id, atom) }));
        return { mod, cells, active: cells.some((c) => c.res.effect !== "none") };
      }),
    [statements],
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {rows.map(({ mod, cells, active }) => {
        const ModIcon = mod.icon;
        return (
          <div key={mod.id} className="flex items-center gap-2">
            <div
              className={cn(
                "grid h-[22px] w-[22px] shrink-0 place-items-center rounded border",
                active
                  ? "border-border bg-muted text-muted-foreground"
                  : "border-transparent text-muted-foreground/50",
              )}
            >
              <ModIcon size={12} />
            </div>
            <span
              className={cn(
                "w-[150px] shrink-0 truncate text-[11.5px] font-medium",
                active ? "text-foreground" : "text-muted-foreground/60",
              )}
              title={mod.id}
            >
              {t(mod.labelKey)}
            </span>
            <div className="flex flex-wrap gap-1">
              {cells.map(({ atom, res }) => {
                const denyWins = res.effect === "deny" && res.overridden.length > 0;
                const risky = mod.risky?.[atom];
                return (
                  <span
                    key={atom}
                    title={`${mod.id}:${atom} → ${res.effect}${denyWins ? " (deny > allow)" : ""}`}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px font-mono text-[9.5px]",
                      res.effect === "allow" && "border-primary/30 bg-primary/10 text-primary",
                      res.effect === "deny" && "border-destructive/40 bg-destructive/10 text-destructive",
                      res.effect === "none" && "border-border text-muted-foreground/50",
                    )}
                  >
                    {res.effect === "allow" && <Check size={8} />}
                    {res.effect === "deny" && <X size={8} />}
                    {res.effect === "none" && risky && <AlertTriangle size={8} className="text-warning/70" />}
                    {atom}
                    {denyWins ? "*" : ""}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
